/**
 * Phase 278 — Precision-safe Indian-rupee compact formatter.
 *
 * The naive shortcut `(amount / 1000).toFixed(0) + 'K'` inflates 9,500 to
 * "10K" because `(9.5).toFixed(0) === "10"`.  That's an accounting bug:
 * the card claims more money was collected than the ledger actually shows.
 *
 * This formatter:
 *   • Preserves a 1-decimal precision when the value is not a clean
 *     multiple of the unit (9,500 → ₹9.5K, never ₹10K).
 *   • Snaps to the natural Indian numbering scale: K (thousands),
 *     L (lakhs = 1,00,000), Cr (crores = 1,00,00,000).
 *   • Falls back to a fully-localized string for sub-thousand values
 *     so amounts like ₹450 don't render as "₹0K".
 *
 * Examples:
 *   formatINRCompact(450)      -> "₹450"
 *   formatINRCompact(9500)     -> "₹9.5K"  // NOT "₹10K"
 *   formatINRCompact(10000)    -> "₹10K"
 *   formatINRCompact(125000)   -> "₹1.3L"
 *   formatINRCompact(15000000) -> "₹1.5Cr"
 */
export function formatINRCompact(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n === 0) return '₹0';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);

  // < 1,000 — render full integer with Indian grouping (no info loss).
  if (abs < 1000) return `${sign}₹${Math.round(abs).toLocaleString('en-IN')}`;

  let unit;
  let value;
  if (abs < 100000) {                    // 1K – 99,999
    value = abs / 1000;
    unit = 'K';
  } else if (abs < 10000000) {           // 1L – 99,99,999
    value = abs / 100000;
    unit = 'L';
  } else {                                // 1Cr +
    value = abs / 10000000;
    unit = 'Cr';
  }
  // Render with one decimal, then strip any trailing ".0" so 10.0K → 10K
  // even when the source was 9,999 (which rounds to 10.0 at one-decimal
  // precision but should still display as a clean unit).
  const rendered = value.toFixed(1).replace(/\.0$/, '');
  return `${sign}₹${rendered}${unit}`;
}
